import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, notifyPropertyChange, setProperties } from '@ember/object';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import { fn } from '@ember/helper';
import isPending from '../helpers/is-pending.js';
import has from '../helpers/has.js';
import sortFn from '../helpers/sort-fn.js';
import Icon from './icon.gjs';
import Sortable from './sortable.gjs';
import PageHeader from './page-header.gjs';
import PageContent from './page-content.gjs';
import PageInfo from './page-info.gjs';
import EditPermissionDialog from './edit-permission-dialog.gjs';
import {
  faShieldHalved,
  faTrash,
  faPlus,
  faRefresh,
  faPencil,
} from '../icons/solid.js';

export default class PermissionsComponent extends Component {
  @service session;
  @service time;
  @service notify;
  @service intl;
  @service requestManager;

  @tracked permissions = [];
  @tracked permissionsPromise = null;
  @tracked selectedPermissionIds = new Set();
  @tracked sortBy = 'subject:asc';

  @tracked permissionDialogModel = null;
  @tracked showPermissionDialog = false;

  constructor() {
    super(...arguments);
    this.refreshPermissions();
  }

  get selectablePermissions() {
    return this.permissions.filter((x) => !x.isBuiltIn);
  }

  get selectedPermissions() {
    return this.selectablePermissions.filter((x) =>
      this.selectedPermissionIds.has(x.id),
    );
  }

  get allPermissionsSelected() {
    return (
      this.selectablePermissions.length &&
      this.selectedPermissions.length === this.selectablePermissions.length
    );
  }

  get somePermissionsSelected() {
    return (
      this.selectedPermissions.length &&
      this.selectedPermissions.length < this.selectablePermissions.length
    );
  }

  get sortedPermissions() {
    return this.permissions.sort(sortFn(this.sortBy));
  }

  @action async refreshPermissions() {
    this.permissionsPromise = this.getPermissions();
  }

  @action async getPermissions() {
    const { content } = await this.requestManager.request({
      url: '/api/permissions',
    });
    this.permissions = content;
  }

  @action select(permission) {
    if (this.selectedPermissionIds.has(permission.id)) {
      this.selectedPermissionIds.delete(permission.id);
    } else {
      this.selectedPermissionIds.add(permission.id);
    }
    notifyPropertyChange(this, 'selectedPermissionIds');
  }

  @action selectAll() {
    if (this.allPermissionsSelected) {
      this.selectedPermissionIds.clear();
    } else {
      this.selectablePermissions.forEach((x) =>
        this.selectedPermissionIds.add(x.id),
      );
    }
    notifyPropertyChange(this, 'selectedPermissionIds');
  }

  @action newPermission() {
    this.permissionDialogModel = null;
    this.showPermissionDialog = true;
  }

  @action editPermission(permission) {
    this.permissionDialogModel = permission;
    this.showPermissionDialog = true;
  }

  @action async createPermission(attributes) {
    try {
      this.permissionsPromise = this.requestManager.request({
        url: '/api/permissions',
        method: 'POST',
        headers: new Headers([['Content-Type', 'application/json']]),
        body: JSON.stringify(attributes),
      });
      const { content } = await this.permissionsPromise;
      this.permissions = [...this.permissions, content];
      this.notify.alert(this.intl.t('permissions.create-success'));
    } catch (error) {
      this.notify.alert(this.intl.t('permissions.create-error'), error);
    }
  }

  @action async updatePermission(attributes) {
    const permission = this.permissionDialogModel;
    setProperties(permission, attributes);
    try {
      this.permissionsPromise = this.requestManager.request({
        url: `/api/permissions/${permission.id}`,
        method: 'PUT',
        headers: new Headers([['Content-Type', 'application/json']]),
        body: JSON.stringify(permission),
      });
      const { content } = await this.permissionsPromise;
      setProperties(permission, content);
      this.notify.alert(this.intl.t('permissions.update-success'));
    } catch (error) {
      this.notify.alert(this.intl.t('permissions.update-error'), error);
      this.refreshPermissions();
    }
  }

  @action async deletePermissions() {
    for (const permission of this.selectedPermissions) {
      try {
        this.permissionsPromise = this.requestManager.request({
          url: `/api/permissions/${permission.id}`,
          method: 'DELETE',
        });

        await this.permissionsPromise;

        this.permissions = this.permissions.filter(
          (x) => x.id !== permission.id,
        );
        this.selectedPermissionIds.delete(permission.id);
        notifyPropertyChange(this, 'selectedPermissionIds');
      } catch (error) {
        this.notify.alert(
          this.intl.t('permissions.delete-error', {
            permissionname: permission.permissionname,
          }),
          error,
        );
        this.refreshPermissions();
        return;
      }
    }

    this.notify.alert(this.intl.t('permissions.delete-success'));
  }

  <template>
    <PageHeader
      @icon={{faShieldHalved}}
      @title={{t "permissions.title"}}
      @promise={{this.permissionsPromise}}
      @onClose={{@onClose}}
    />

    <PageInfo>
      {{t "permissions.description"}}
    </PageInfo>

    <PageContent>
      <:tools>
        {{#if this.selectedPermissions.length}}
          <button
            type="button"
            class="btn btn-ghost text-primary uppercase"
            {{on "click" this.deletePermissions}}
          >
            <Icon @icon={{faTrash}} />
            {{t "delete"}}
            <span class="badge badge-sm">
              {{this.selectedPermissions.length}}
            </span>
          </button>
        {{else}}
          <button
            type="button"
            class="btn btn-ghost text-primary uppercase"
            {{on "click" this.newPermission}}
          >
            <Icon @icon={{faPlus}} />
            {{t "permissions.new"}}
          </button>
        {{/if}}

        <div class="flex-1" />

        <button
          type="button"
          class="btn btn-ghost btn-square text-accent tooltip tooltip-left"
          data-tip={{t "refresh"}}
          {{on "click" this.refreshPermissions}}
        >
          <Icon @icon={{faRefresh}} />
        </button>
      </:tools>

      <:content>
        <table class="table table-pin-rows table-xs">
          <thead>
            <tr>
              <th class="w-10">
                <input
                  type="checkbox"
                  class="checkbox checkbox-primary"
                  disabled={{not this.selectablePermissions.length}}
                  checked={{this.allPermissionsSelected}}
                  indeterminate={{this.somePermissionsSelected}}
                  {{on "click" this.selectAll}}
                />
              </th>
              <th class="w-96">
                <Sortable
                  @value={{this.sortBy}}
                  @onChange={{fn (mut this.sortBy)}}
                  @name="subject"
                >
                  {{t "permissions.subject"}}
                </Sortable>
              </th>
              <th class="w-10"></th>
              <th class="w-48">
                <Sortable
                  @value={{this.sortBy}}
                  @onChange={{fn (mut this.sortBy)}}
                  @name="permission"
                >
                  {{t "permissions.permission"}}
                </Sortable>
              </th>
              <th class="w-64">
                <Sortable
                  @value={{this.sortBy}}
                  @onChange={{fn (mut this.sortBy)}}
                  @name="objectType"
                >
                  {{t "permissions.object-type"}}
                </Sortable>
              </th>
              <th class="w-96">
                <Sortable
                  @value={{this.sortBy}}
                  @onChange={{fn (mut this.sortBy)}}
                  @name="object"
                >
                  {{t "permissions.object"}}
                </Sortable>
              </th>
            </tr>
          </thead>
          <tbody>
            {{#each this.sortedPermissions key="id" as |permission|}}
              <tr class="hover">
                <th>
                  <input
                    type="checkbox"
                    class="checkbox checkbox-primary"
                    checked={{has this.selectedPermissionIds permission.id}}
                    disabled={{permission.isBuiltIn}}
                    {{on "click" (fn this.select permission)}}
                  />
                </th>
                <td class="text-base font-bold text-primary">
                  {{permission.subject}}
                </td>
                <td>
                  {{#unless permission.isBuiltIn}}
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm text-primary show-on-hover"
                      {{on "click" (fn this.editPermission permission)}}
                    >
                      <Icon @icon={{faPencil}} />
                    </button>
                  {{/unless}}
                </td>
                <td class="text-sm">
                  {{permission.permission}}
                </td>
                <td class="text-sm">
                  {{permission.objectType}}
                </td>
                <td class="text-sm">
                  {{permission.object}}
                </td>
              </tr>
            {{else}}
              {{#unless (isPending this.permissionsPromise)}}
                <tr>
                  <td colspan="3" class="text-sm">
                    {{t "empty-folder-message"}}
                  </td>
                </tr>
              {{/unless}}
            {{/each}}
          </tbody>
        </table>
      </:content>
    </PageContent>

    {{#if this.showPermissionDialog}}
      <EditPermissionDialog
        @model={{this.permissionDialogModel}}
        @onSubmit={{if
          this.permissionDialogModel
          this.updatePermission
          this.createPermission
        }}
        @onClose={{fn (mut this.showPermissionDialog) false}}
      />
    {{/if}}
  </template>
}
